import React, { useEffect, useState } from "react";
import { Button, Col } from "reactstrap";
import { useTranslation } from "react-i18next";
import useApiCallService from "../../Common/Helpers/ApiCall";
import SortableList from "../../Common/Components/Lists/SortableList";
import { CustomButton } from "../../Common/Components/Button/CustomButton";
import { CustomModal } from "../../Common/Components/Modal/CustomModal";
import { SlidePanel } from "../../Common/Components/Modal/SlidePanel";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { CustomReload } from "../../AppSystem/Helpers/CustomReload";
import { CheckUserPermission } from "../../AppSystem/Helpers/CheckUserPermission";
import { CheckMobile } from "../../AppSystem/Helpers/CheckMobile";
import { useHeader } from "../../Auth/Components/Nav/HeaderContext";
import { ADMIN_PATHS } from "../../config";
import ResponseApiKey, { API_KEY_STATUS } from "../Models/Response/ResponseApiKey";
import ApiKeyCreatePanel from "../Components/ApiKeyCreatePanel";
import ApiKeyInfo from "../Components/ApiKeyInfo";

const AdminApiKeyList = () => {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();
    const { setPageTitle, setPageTitleKey, setOnBack } = useHeader();
    const isMobile = CheckMobile(998);

    useEffect(() => {
        setPageTitle(t('apiKey.apiKeys'));
        setPageTitleKey('apiKey.apiKeys');
        setOnBack(null);
        return () => { setPageTitle(''); setPageTitleKey(''); setOnBack(null); };
    }, [t]);

    const permissionCreate = CheckUserPermission("system.setting.api-key.create") || CheckUserPermission("system.setting.api-key") || CheckUserPermission("system.setting") || CheckUserPermission("system");
    const permissionRevoke = CheckUserPermission("system.setting.api-key.revoke") || CheckUserPermission("system.setting.api-key") || CheckUserPermission("system.setting") || CheckUserPermission("system");

    const [createPanelOpen, setCreatePanelOpen] = useState(false);
    // Set when a key has been created, the list is reloaded when the panel is closed
    const [keyCreated, setKeyCreated] = useState(false);

    const [displayRevokeModal, setDisplayRevokeModal] = useState(false);
    const [apiKeyToRevoke, setApiKeyToRevoke] = useState(null);

    const [infoApiKey, setInfoApiKey] = useState(null);

    const closeCreatePanel = () => {
        setCreatePanelOpen(false);
        if (keyCreated) {
            setKeyCreated(false);
            CustomReload(ADMIN_PATHS.ADMIN_API_KEY);
        }
    };

    // HANDLE MODAL REVOKE
    const handleModalRevoke = async () => {
        const apiCallOptions = {
            method: "put",
            url: `/api-key/revoke?id=${apiKeyToRevoke?.id}`,
        };
        try {
            await ApiCall(apiCallOptions, (response) => {
                if (response) {
                    FlashMessage(t('apiKey.revokedSuccessfully'), 'flash-messageSuccess');
                    setTimeout(() => { CustomReload(ADMIN_PATHS.ADMIN_API_KEY); }, 1500);
                }
            }, (errorMessage) => {
                FlashMessage(errorMessage && errorMessage !== 'Error' ? errorMessage : t('validationMessage.sendError'), 'flash-messageError');
            });
        } catch (error) {
            console.log(error);
            FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
        }
        setDisplayRevokeModal(false);
    };

    const modalRevokeContent = (
        <div>
            <p className="mb-2">{t('apiKey.revokeConfirm', { name: apiKeyToRevoke?.name ?? '' })}</p>
            <p className="mb-0 text-muted">{t('apiKey.revokeWarning')}</p>
        </div>
    );

    const modalRevokeFooter = (
        <>
            <Button className="box-green border-0" onClick={handleModalRevoke}>
                {t('apiKey.revoke')}
            </Button>
            <Button className="box-red border-0" onClick={() => setDisplayRevokeModal(false)}>
                {t('button.cancel')}
            </Button>
        </>
    );

    const renderActionColumn = (item) => (
        <>
            <Button
                className="bg-green-light text-green border-0 position-relative reimbursement-report-action-btn"
                onClick={() => setInfoApiKey(item)}
            >
                <i className="bx bx-show fs-5 align-middle pb-1" />
                <div className="tooltip-custom-button">{t("button.info")}</div>
            </Button>
            {permissionRevoke && item.status === API_KEY_STATUS.ACTIVE &&
                <Button
                    className="box-red border-0 position-relative me-2 rounded reimbursement-report-action-btn"
                    onClick={() => {
                        setApiKeyToRevoke(item);
                        setDisplayRevokeModal(true);
                    }}
                >
                    <i className="bx bx-block fs-5 align-middle pb-1" />
                    <div className="tooltip-custom-button">{t('apiKey.revoke')}</div>
                </Button>
            }
        </>
    );

    const createButton = permissionCreate ? (
        <CustomButton
            buttonWidth="block"
            customClasses="justify-content-center reimbursement-create-btn"
            text={t('apiKey.newApiKey')}
            icon="bx bxs-plus-circle pe-2"
            buttonColor="#084F35"
            hoverButtonColor="#157553"
            onClick={() => setCreatePanelOpen(true)}
            buttonSize="big"
        />
    ) : null;

    const baseUrl = "/api-key/search/filter";
    const columns = ["visualId", "name", "prefix", "status", "expiresAt", "lastUsedAt", "createdByName", "createdAt"];

    const listOptions = {
        "model": ResponseApiKey,
        "columns": columns,
        "actions": {
            "actionColumn": renderActionColumn,
        },
        "exportIsAvailable": false,
        "filterTestIsAvailable": true,
        "nameFile": t('apiKey.apiKeys'),
        "modelType": "ApiKey",
        "ignoreFields": columns,
        "urlGetSearch": baseUrl,
        "viewName": "AdminApiKeyList",
    };

    return (
        <>
            <CustomModal
                isOpen={displayRevokeModal}
                toggle={() => setDisplayRevokeModal(!displayRevokeModal)}
                body={modalRevokeContent}
                footer={modalRevokeFooter}
            />

            <SlidePanel
                isOpen={createPanelOpen}
                onClose={closeCreatePanel}
                title={t('apiKey.newApiKey')}
                closeOnOutsideClick={false}
                variant="center"
            >
                {createPanelOpen &&
                    <ApiKeyCreatePanel
                        onCreated={() => setKeyCreated(true)}
                        onClose={closeCreatePanel}
                    />
                }
            </SlidePanel>

            <SlidePanel
                isOpen={infoApiKey !== null}
                onClose={() => setInfoApiKey(null)}
                title={t('apiKey.apiKey') + (infoApiKey?.visualId ? ' ' + infoApiKey.visualId : '')}
            >
                {infoApiKey && <ApiKeyInfo apiKey={infoApiKey} />}
            </SlidePanel>

            <Col lg="12" className={`mb-5 ${isMobile ? `p-0 ${createButton ? "rhm-wrapper-with-create" : ""}` : "p-4 mx-0 px-0 pt-0"}`}>
                <div className="rh-wrapper">
                    <div className="rh-inner pt-0">
                        <div className="rh-header-row">
                            <div>
                                {/* On mobile the title is already shown in the top bar, like ReportProceedingList */}
                                {!isMobile && <h2 className="pb-2 ps-1 rokkitt-font mb-0">{t('apiKey.apiKeys')}</h2>}
                                <p className="ps-1 mb-0" style={{ fontSize: 13, color: '#6B7280' }}>{t('apiKey.description')}</p>
                            </div>
                            {/* Desktop: button in the header. Mobile: fixed at the bottom, like ReportProceedingList */}
                            {createButton && !isMobile && <div>{createButton}</div>}
                        </div>
                    </div>
                </div>

                <SortableList listOptions={listOptions} />

                {createButton && isMobile && <div className="rhm-create-btn-wrap">{createButton}</div>}
            </Col>
        </>
    );
};

export default AdminApiKeyList;
